/**
 * 来場者スタンプ帳ローカルキャッシュ
 * RPCが利用できない場合や表示高速化のため、達成済み企画(event_id)を端末に保存します。
 */

import webStorage from '../../../shared/services/webStorage';
import { AppError } from '../../../shared/utils/errorHandling';
import { logWarn } from '../../../shared/utils/logger';
import { validateRequiredString } from '../../../shared/utils/validation';

/** ローカル保存キー接頭辞 */
const STAMP_VISITS_STORAGE_KEY_PREFIX = '@ikomasai/stamp_visits/';

/** 端末が覚えている周の保存キー接頭辞 */
const STAMP_ROUND_STORAGE_KEY_PREFIX = '@ikomasai/stamp_round/';

/** キャッシュ保存失敗エラーコード */
export const VISITOR_STAMP_CACHE_SAVE_FAILED_CODE = 'VISITOR_STAMP_CACHE_SAVE_FAILED';

/** キャッシュ読み込み失敗エラーコード */
export const VISITOR_STAMP_CACHE_LOAD_FAILED_CODE = 'VISITOR_STAMP_CACHE_LOAD_FAILED';

/**
 * visitorId からストレージキーを作る
 * @param {string} visitorId - 来場者ID
 * @returns {string} ストレージキー
 */
const toStorageKey = (visitorId) => `${STAMP_VISITS_STORAGE_KEY_PREFIX}${visitorId}`;

/**
 * visits配列の簡易検証
 * @param {any} visits - visits
 * @returns {Array<{ eventId: string, claimedAt: string }>} 正規化済み配列
 */
const normalizeVisits = (visits) => {
  if (!Array.isArray(visits)) {
    return [];
  }

  return visits
    .map((visit) => ({
      eventId: typeof visit?.eventId === 'string' ? visit.eventId : '',
      claimedAt: typeof visit?.claimedAt === 'string' ? visit.claimedAt : '',
    }))
    .filter((visit) => Boolean(visit.eventId) && Boolean(visit.claimedAt));
};

/**
 * ローカルの達成済み企画一覧を取得する
 * @param {string} visitorId - 来場者ID
 * @returns {Promise<Array<{ eventId: string, claimedAt: string }>>} 達成済み企画
 */
export const loadLocalEventVisits = async (visitorId) => {
  const visitorIdValidation = validateRequiredString(visitorId, 'visitorId');

  if (!visitorIdValidation.isValid) {
    return [];
  }

  try {
    const serialized = await webStorage.getItem(toStorageKey(visitorId.trim()));

    if (!serialized) {
      return [];
    }

    try {
      const parsed = JSON.parse(serialized);
      return normalizeVisits(parsed);
    } catch (error) {
      logWarn('スタンプ帳キャッシュが破損していたため削除します。', {
        message: error instanceof Error ? error.message : String(error),
      });
      await webStorage.removeItem(toStorageKey(visitorId.trim()));
      return [];
    }
  } catch (error) {
    throw new AppError('スタンプ帳キャッシュの読み込みに失敗しました。', {
      code: VISITOR_STAMP_CACHE_LOAD_FAILED_CODE,
      statusCode: 500,
      details: {
        message: error instanceof Error ? error.message : String(error),
      },
      cause: error instanceof Error ? error : undefined,
    });
  }
};

/**
 * ローカル達成済み企画一覧を上書き保存する
 * @param {string} visitorId - 来場者ID
 * @param {Array<{ eventId: string, claimedAt: string }>} visits - 達成済み企画
 * @returns {Promise<void>}
 */
export const replaceLocalEventVisits = async (visitorId, visits) => {
  const visitorIdValidation = validateRequiredString(visitorId, 'visitorId');

  if (!visitorIdValidation.isValid) {
    return;
  }

  const normalizedVisits = normalizeVisits(visits);

  try {
    await webStorage.setItem(toStorageKey(visitorId.trim()), JSON.stringify(normalizedVisits));
  } catch (error) {
    throw new AppError('スタンプ帳キャッシュの保存に失敗しました。', {
      code: VISITOR_STAMP_CACHE_SAVE_FAILED_CODE,
      statusCode: 500,
      details: {
        message: error instanceof Error ? error.message : String(error),
      },
      cause: error instanceof Error ? error : undefined,
    });
  }
};

/**
 * 単一の達成済み企画をローカルへ追加(重複は更新)する
 * @param {string} visitorId - 来場者ID
 * @param {string} eventId - イベントID
 * @param {string} claimedAt - 達成日時(ISO)
 * @returns {Promise<void>}
 */
export const upsertLocalEventVisit = async (visitorId, eventId, claimedAt) => {
  const visitorIdValidation = validateRequiredString(visitorId, 'visitorId');
  const eventIdValidation = validateRequiredString(eventId, 'eventId');
  const claimedAtValidation = validateRequiredString(claimedAt, 'claimedAt');

  if (!visitorIdValidation.isValid || !eventIdValidation.isValid || !claimedAtValidation.isValid) {
    return;
  }

  const currentVisits = await loadLocalEventVisits(visitorId.trim());
  const nextVisits = [...currentVisits];
  const existingIndex = nextVisits.findIndex((visit) => visit.eventId === eventId.trim());

  if (existingIndex >= 0) {
    nextVisits[existingIndex] = { eventId: eventId.trim(), claimedAt: claimedAt.trim() };
  } else {
    nextVisits.push({ eventId: eventId.trim(), claimedAt: claimedAt.trim() });
  }

  await replaceLocalEventVisits(visitorId.trim(), nextVisits);
};

/**
 * 端末が覚えている周とサーバーの周を揃える
 * 周が変わると、前の周の達成キャッシュがスタンプ帳に混ざらないよう消す。保存済みの周が無ければ1周目として扱う。
 * @param {string} visitorId - 来場者ID
 * @param {number} round - サーバーの現在の周
 * @returns {Promise<void>}
 * @throws {AppError} 端末の読み書きに失敗した場合
 */
export const syncLocalStampRound = async (visitorId, round) => {
  const visitorIdValidation = validateRequiredString(visitorId, 'visitorId');

  if (!visitorIdValidation.isValid) {
    return;
  }

  const normalizedVisitorId = visitorId.trim();
  const roundStorageKey = `${STAMP_ROUND_STORAGE_KEY_PREFIX}${normalizedVisitorId}`;

  try {
    const savedRound = (await webStorage.getItem(roundStorageKey)) ?? '1';

    if (savedRound === String(round)) {
      return;
    }

    // 周の保存を後にすることで、消す前に失敗しても次回もう一度消せる。
    await webStorage.removeItem(toStorageKey(normalizedVisitorId));
    await webStorage.setItem(roundStorageKey, String(round));
  } catch (error) {
    throw new AppError('スタンプ帳キャッシュの更新に失敗しました。', {
      code: VISITOR_STAMP_CACHE_SAVE_FAILED_CODE,
      statusCode: 500,
      details: {
        message: error instanceof Error ? error.message : String(error),
      },
      cause: error instanceof Error ? error : undefined,
    });
  }
};
